import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { ContactDataTemplate } from '../subComponents/ContactDataTemplate';
import { useListInternalOrderContactsQuery } from '../../../../API/order/contacts/InternalContactsApi';
import { IInternalContactData, IPresentationInternalContact } from '../../../../types/contacts/IInternalContact';
import { getListOfPresentationContactsWithRoles } from '../../../../types/contacts/IPresentationContactData';
import { InternalOrderContactsTable } from './InternalOrderContactsTable';
import { AddInternalContactContainer } from './AddInternalContactContainer';
import { EditInternalContactContainer } from './EditInternalContactContainer';
import { useListRolesQuery } from '../../../../API/roles/RolesApi';
import { Show } from '../../../shared/Show';

export const InternalContactsContainer: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data = [], error, isLoading } = useListInternalOrderContactsQuery(orderId);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [editContact, setEditContact] = useState<IInternalContactData | null>(null);
  const { data: roles = [] } = useListRolesQuery();

  const presentationInternalContacts: IPresentationInternalContact[] = useMemo(() => {
    return getListOfPresentationContactsWithRoles(
      data,
      roles.map((role) => role.name),
    );
  }, [data, roles]);

  const addContact = useCallback(() => {
    setShowAddContactForm(true);
  }, [setShowAddContactForm]);

  const cancelAddContact = useCallback(() => {
    setShowAddContactForm(false);
  }, [setShowAddContactForm]);

  const onSelectExistingAdminContact = useCallback(
    (adminId: UUID) => {
      const adminContact = presentationInternalContacts.find((contact) => contact.admin.id === adminId);
      if (adminContact) {
        setShowAddContactForm(false);
        setEditContact(adminContact);
      }
    },
    [presentationInternalContacts, setShowAddContactForm]
  );

  return (
    <>
      <ContactDataTemplate
        header={'Internal Contacts'}
        hintText={'Spruce team members working on this order.'}
        rtkError={error}
        addContact={addContact}
      >
        <Show when={!!roles.length}>
          <InternalOrderContactsTable
            data={presentationInternalContacts}
            loading={isLoading}
            onEditContact={setEditContact}
            roles={roles}
          />
        </Show>
      </ContactDataTemplate>
      <AddInternalContactContainer
        cancelAddContact={cancelAddContact}
        showAddContactForm={showAddContactForm}
        onSelectExistingAdminContact={onSelectExistingAdminContact}
      />
      <EditInternalContactContainer editContact={editContact} setEditContact={setEditContact} />
    </>
  );
};
